
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../AppContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import LoadingSpinner from '../LoadingSpinner';
import { motion } from 'framer-motion';
import NexusSelectionSkeleton from './NexusSelectionSkeleton';
import { useTranslation } from 'react-i18next';

const NexusSelection = ({ onNexusSelected }) => {
    const { t } = useTranslation();
    const { db, user, showConfirmation } = useAppContext();
    const [nexuses, setNexuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedNexusId, setSelectedNexusId] = useState(null);
    const functions = getFunctions();

    useEffect(() => {
        if (!db || !user) {
            setLoading(false);
            return;
        }

        const fetchNexuses = async () => {
            try {
                const nexusesCollection = collection(db, `artifacts/whispers-of-harmony/public/data/nexuses`);
                const q = query(nexusesCollection, where('memberIds', 'array-contains', user.uid));
                const querySnapshot = await getDocs(q);
                const userNexuses = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setNexuses(userNexuses);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchNexuses();
    }, [db, user]);

    const handleSelectNexus = (nexusId) => {
        if (!db || !user) return;

        const doChange = async () => {
            try {
                const changePrimaryNexusFn = httpsCallable(functions, 'changePrimaryNexus');
                await changePrimaryNexusFn({ nexusId });
                setSelectedNexusId(nexusId);
                setTimeout(() => {
                    onNexusSelected(nexusId);
                }, 1500);
            } catch (error) {
                console.error("Error changing primary nexus:", error);
            }
        };

        if (user.primaryShowdownNexus) {
            showConfirmation({
                title: t('showdown.nexusSelection.changePrimaryNexusTitle'),
                message: t('showdown.nexusSelection.changePrimaryNexusMessage'),
                confirmText: t('showdown.nexusSelection.confirmChange'),
                cancelText: t('showdown.nexusSelection.cancel'),
                onConfirm: doChange,
            });
        } else {
            doChange();
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    if (loading) return (
        <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden items-center justify-center">
            <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 font-orbitron drop-shadow-sm">{t('showdown.nexusSelection.title')}</h1>
                <p className="text-gray-300 mt-2">{t('showdown.nexusSelection.subtitle')}</p>
            </div>
            <NexusSelectionSkeleton />
        </div>
    );
    if (error) return (
        <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden items-center justify-center">
            <p className="text-red-500 font-bold">{t('showdown.nexusSelection.error')}</p>
        </div>
    );

    if (selectedNexusId) {
        return (
            <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden items-center justify-center text-center">
                <h1 className="text-3xl font-bold text-green-400 mb-4 animate-pulse font-orbitron">{t('showdown.nexusSelection.selectedTitle')}</h1>
                <p className="text-lg text-gray-300">{t('showdown.nexusSelection.enteringLobby')}</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden">
            <div className="text-center mb-10 mt-10">
                <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 font-orbitron drop-shadow-sm">{t('showdown.nexusSelection.title')}</h1>
                <p className="text-gray-300 mt-3 text-lg font-light">{t('showdown.nexusSelection.subtitle')}</p>
            </div>
            <motion.div 
                className="w-full max-w-3xl mx-auto space-y-4 overflow-y-auto custom-scrollbar pb-20"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {nexuses.map(nexus => (
                    <motion.div key={nexus.id} variants={itemVariants}>
                        <div className="bg-gray-800/40 backdrop-blur-md border border-white/10 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between hover:bg-gray-800/60 transition-all group shadow-lg">
                            <div className="mb-4 md:mb-0 md:mr-6 text-center md:text-left">
                                <h2 className="text-xl font-bold text-white group-hover:text-cyan-300 transition-colors font-orbitron">{nexus.name}</h2>
                                <p className="text-gray-400 text-sm mt-1 max-w-md">{nexus.description}</p>
                            </div>
                            <button 
                                onClick={() => handleSelectNexus(nexus.id)}
                                className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 rounded-lg font-bold text-sm transition-all flex-shrink-0 shadow-lg shadow-cyan-500/20 border border-white/10 hover:scale-105"
                            >
                                {t('showdown.nexusSelection.select')}
                            </button>
                        </div>
                    </motion.div>
                ))}
            </motion.div>
        </div>
    );
};

export default NexusSelection;
